import { useQueryClient, useQuery, useMutation, UseMutationOptions } from 'react-query';

import { deleteRequest, getRequest, postRequest, putRequest } from 'services/apiRequests';
import type { StringifiedUUID } from 'types';
import { SPMModel, SPMModelRequest } from 'types/SPM';
import type { APIError } from 'utils/errors';

const apiUrl = `/api/v1/spm/models/`;

const getSPMModel = (spmModelId: StringifiedUUID) => getRequest<null, SPMModel>(`${apiUrl}${spmModelId}/`);

const useGetSPMModelQuery = (spmModelId: StringifiedUUID) => {
  const QUERY_KEY = ['spm-models', spmModelId];
  const queryClient = useQueryClient();

  const query = useQuery<SPMModel, APIError>(QUERY_KEY, () => getSPMModel(spmModelId));
  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

const createSPMModel = (csrfToken: string) => (spmModel: SPMModelRequest) => {
  return postRequest<SPMModelRequest, SPMModel>(apiUrl, spmModel, csrfToken);
};

const useCreateSPMModelMutation = (
  csrfToken: string,
  options: UseMutationOptions<SPMModel, APIError, SPMModelRequest> = {},
) => {
  return {
    mutation: useMutation<SPMModel, APIError, SPMModelRequest>(createSPMModel(csrfToken), options),
  };
};

type UpdateSPMModelProps = {
  spmModelId: StringifiedUUID;
  spmModel: SPMModelRequest;
};

const updateSPMModel =
  (csrfToken: string) =>
  ({ spmModelId, spmModel }: UpdateSPMModelProps) => {
    return putRequest<SPMModelRequest, SPMModel>(`${apiUrl}${spmModelId}/`, spmModel, csrfToken);
  };

const useUpdateSPMModelMutation = (
  csrfToken: string,
  options: UseMutationOptions<SPMModel, APIError, UpdateSPMModelProps> = {},
) => {
  return {
    mutation: useMutation<SPMModel, APIError, UpdateSPMModelProps>(updateSPMModel(csrfToken), options),
  };
};

const deleteSPMModel = (csrfToken: string) => (spmModelId: StringifiedUUID) => {
  return deleteRequest<null, void>(`${apiUrl}${spmModelId}/`, null, csrfToken);
};

const useDeleteSPMModelMutation = (
  csrfToken: string,
  options: UseMutationOptions<void, APIError, StringifiedUUID> = {},
) => {
  return {
    mutation: useMutation<void, APIError, StringifiedUUID>(deleteSPMModel(csrfToken), options),
  };
};

export const SPMModelsService = {
  apiUrl,
  useGetSPMModelQuery,
  useCreateSPMModelMutation,
  useUpdateSPMModelMutation,
  useDeleteSPMModelMutation,
};
